/** Section 25 - creature abilities as hooks over the closed union.
 *
 * There is exactly one resolution order, `ABILITY_ORDER`, and every hook walks
 * it. Within one hook two abilities never race, because a creature's ability
 * list is sorted into that order before it is walked and stacks are visited by
 * ascending slot index. No rule in this file names a creature (D9).
 *
 * The order is: prevention first (things that stop the hit happening), then
 * damage shaping, then on-hit statuses, then drains and reflects, then death
 * effects. Reading it top to bottom is reading what happens to a blow.
 */

import { asModifierSrcId } from "../ids.js";
import type { CreatureDefId } from "../ids.js";
import type { Modifier } from "../modifiers.js";
import { resolveStat } from "../modifiers.js";
import { BP_ONE, roundHalfAway } from "../numeric.js";
import { type ContentRegistry, type CreatureAbility, must } from "../content/registry.js";
import {
  BF_HEIGHT, BF_WIDTH, F_ALIVE, F_BERSERK, F_BLINDED, F_NON_LIVING,
  F_PETRIFIED, F_TWO_HEX,
  F_UNDEAD, type BattleState, hexCol, hexDistance, hexIndex, hexRow, isAlive,
} from "./state.js";
import { F2_AFRAID, F2_BOUND, F_HYPNOTIZED, F_REBORN } from "./flags.js";
import { STAT_DAMAGE_TAKEN, VULNERABILITY_CAP_FP } from "./damage.js";
import { STAT_BERSERK, STAT_FIRE_SHIELD, STAT_HYPNOSIS, STAT_LIFE_DRAIN, STAT_BLIND,
} from "./stats.js";
import {
  type Ctx, type DamageResult, addMod, clearFlags, creditKills, damageStack,
  effectiveMaxHp, healStack, removeModsBySource, rollChanceBp, setFlags, setFlags2,
} from "./apply.js";
import { jSetI32, jSetField } from "./journal.js";
import { stackHexes, stacksAdjacent } from "./move.js";
import { artilleryStrikes } from "./machines.js";

export type AbilityKind = CreatureAbility["kind"];

/** The one fixed resolution order. Adding an ability means adding it here. */
export const ABILITY_ORDER: readonly AbilityKind[] = [
  // prevention / structural
  "MAGIC_IMMUNITY", "SPELL_IMMUNITY", "SPELL_VULNERABILITY", "MAGIC_RESISTANCE",
  "NO_RETALIATION", "UNLIMITED_RETALIATION", "EXTRA_RETALIATIONS",
  "FLYING", "TELEPORT", "TWO_HEX", "SHOOTER", "NO_MELEE_PENALTY", "UNDEAD",
  "NON_LIVING", "FEARLESS", "SIEGE_MACHINE",
  // damage shaping
  "JOUSTING", "HATES", "PACK_BOND", "SOUL_HARVEST", "DOUBLE_ATTACK", "MAGIC_ATTACK",
  "ATTACK_ALL_AROUND", "THREE_HEX_BREATH",
  "RANGED_SPREAD", "STRIKE_AND_RETURN",
  // on-hit statuses
  "AGING", "DISEASE", "ACID_BREATH", "BINDING", "CURSE_ON_HIT", "POISON", "DISPEL_ON_HIT",
  "FEAR", "CONFUSION", "CHARM",
  "BLIND_ON_HIT", "PARALYZE", "PETRIFY", "FREEZING_SHOT", "DEATH_STARE", "ACCURATE_SHOT",
  // drains and reflects
  "LIFE_DRAIN", "FIRE_SHIELD",
  // upkeep and death
  "REGENERATE", "RESURRECT_SELF", "DEATH_CLOUD",
  "MORALE_AURA", "LUCK_AURA", "REDUCE_ENEMY_MORALE", "REDUCE_ENEMY_LUCK",
  "CASTER", "SUMMONER",
];

const ORDER_INDEX: Readonly<Record<string, number>> = (() => {
  const m: Record<string, number> = {};
  for (let i = 0; i < ABILITY_ORDER.length; i++) m[ABILITY_ORDER[i]!] = i;
  return m;
})();

const cache = new WeakMap<object, Map<string, readonly CreatureAbility[]>>();

/** A creature's abilities in `ABILITY_ORDER`. Cached: the sort is per definition, not per blow. */
export function abilitiesOf(content: ContentRegistry, def: CreatureDefId): readonly CreatureAbility[] {
  let byDef = cache.get(content);
  if (byDef === undefined) { byDef = new Map(); cache.set(content, byDef); }
  const hit = byDef.get(def as string);
  if (hit !== undefined) return hit;
  const list = [...must(content.creatures, def, "creature").abilities]
    .sort((a, b) => (ORDER_INDEX[a.kind] ?? 999) - (ORDER_INDEX[b.kind] ?? 999));
  byDef.set(def as string, list);
  return list;
}

export function abilityOf<K extends AbilityKind>(
  content: ContentRegistry, def: CreatureDefId, kind: K,
): Extract<CreatureAbility, { kind: K }> | null {
  for (const a of abilitiesOf(content, def)) {
    if (a.kind === kind) return a as Extract<CreatureAbility, { kind: K }>;
  }
  return null;
}

export function hasAbility(content: ContentRegistry, def: CreatureDefId, kind: AbilityKind): boolean {
  return abilityOf(content, def, kind) !== null;
}

export const stackHas = (s: BattleState, content: ContentRegistry, slot: number, kind: AbilityKind): boolean =>
  hasAbility(content, s.stacks.def[slot]!, kind);

// ------------------------------------------------------------------- sources

const SRC_AGING = asModifierSrcId("ability.aging");
const SRC_DISEASE = asModifierSrcId("ability.disease");
const SRC_CURSE = asModifierSrcId("ability.curse");
/**
 * Acid, and one source id per blow.
 *
 * Every other on-hit debuff here is a single modifier with a `stackGroup`, so a
 * second application replaces the first. This one has to ACCUMULATE, so each
 * blow adds a modifier of its own and they are told apart by nothing at all -
 * `resolveStat` sums plain `add` modifiers, and that is exactly the wanted
 * behaviour. The source is shared because nothing ever removes them: acid
 * damage to armour does not wear off.
 */
const SRC_ACID = asModifierSrcId("ability.acid_breath");
const SRC_CONFUSION = asModifierSrcId("ability.confusion");
const SRC_CHARM = asModifierSrcId("ability.charm");
const SRC_POISON = asModifierSrcId("ability.poison");
export const SRC_PETRIFY = asModifierSrcId("ability.petrify");
/** Frozen is its own source: the brittleness outlives the lost turn on purpose. */
const SRC_FREEZE = asModifierSrcId("ability.freeze");
/** The classic's Stone Gaze: three rounds of stone, or until struck. */
export const PETRIFY_ROUNDS = 3;
/** SOUL_HARVEST's running Attack. One modifier, raised, never a stack of them. */
export const SRC_HARVEST = asModifierSrcId("ability.soul_harvest");

// -------------------------------------------------------------- round upkeep

/** REGENERATE restores the wounded top creature to full at the start of each round. */
export function onRoundStartAbilities(c: Ctx, content: ContentRegistry, slot: number): void {
  if (!isAlive(c.s.stacks, slot)) return;
  for (const a of abilitiesOf(content, c.s.stacks.def[slot]!)) {
    if (a.kind !== "REGENERATE") continue;
    const max = effectiveMaxHp(c.s, slot);
    healStack(c, slot, max - c.s.stacks.topHp[slot]!, c.s.stacks.count[slot]!);
  }
}

// ------------------------------------------------------------- attack shaping

export interface AttackShape {
  /** Struck a second time by the same action (DOUBLE_ATTACK). */
  readonly strikes: number;
  /** Extra victims that take the same blow and never retaliate. */
  readonly splash: readonly number[];
  /** Attacker returns to where it stood before the blow. */
  readonly returns: boolean;
}

/**
 * ATTACK_ALL_AROUND hits every enemy touching the attacker; THREE_HEX_BREATH
 * hits the hex behind the target on the same axis (and the victim's tail, if it
 * is a two-hex creature, which is the reading that makes "three hexes" literal);
 * RANGED_SPREAD scatters a shot over everything standing near where it lands.
 *
 * A gun worked by an expert Artillery crew fires twice, through the same
 * `strikes` field DOUBLE_ATTACK uses - the machine has no ability of its own for
 * it, because what it is worth is the hero's training and not the machine's.
 */
export function shapeAttack(
  s: BattleState, content: ContentRegistry, attacker: number, target: number, ranged: boolean,
): AttackShape {
  const st = s.stacks;
  const abil = abilitiesOf(content, st.def[attacker]!);
  let strikes = artilleryStrikes(st, attacker);
  let returns = false;
  const splash: number[] = [];

  for (const a of abil) {
    switch (a.kind) {
      case "DOUBLE_ATTACK": strikes = 2; break;
      case "RANGED_SPREAD": {
        // A shot only scatters where it lands, so the spread is measured from
        // the target's hex and never from the shooter's - and it catches the
        // shooter's own side too, which is the classic's rule and the whole reason a
        // Bone Sage is awkward to place.
        if (!ranged) break;
        const at = st.hex[target]!;
        for (let i = 0; i < st.n; i++) {
          if (i === target || i === attacker || !isAlive(st, i)) continue;
          const hexes: number[] = [0, 0];
          const k = stackHexes(st, i, hexes);
          for (let m = 0; m < k; m++) {
            if (hexDistance(hexes[m]!, at) <= a.radiusHexes) { splash.push(i); break; }
          }
        }
        break;
      }
      case "STRIKE_AND_RETURN": if (!ranged) returns = true; break;
      case "ATTACK_ALL_AROUND": {
        if (ranged) break;
        for (let i = 0; i < st.n; i++) {
          if (i === target || i === attacker || !isAlive(st, i)) continue;
          if (st.side[i] === st.side[attacker]) continue;
          if (stacksAdjacent(st, attacker, i)) splash.push(i);
        }
        break;
      }
      case "THREE_HEX_BREATH": {
        if (ranged) break;
        const from = st.hex[attacker]!;
        const to = st.hex[target]!;
        const beyond = breathHex(from, to);
        for (let i = 0; i < st.n; i++) {
          if (i === target || i === attacker || !isAlive(st, i)) continue;
          if (st.side[i] === st.side[attacker]) continue;
          const hexes: number[] = [0, 0];
          const k = stackHexes(st, i, hexes);
          for (let m = 0; m < k; m++) {
            if (hexes[m] === beyond) { splash.push(i); break; }
          }
        }
        break;
      }
      default: break;
    }
  }
  return { strikes, splash, returns };
}

/**
 * The hex one step further along the attacker-to-target axis, in cube space so
 * "further along" means collinear rather than merely further away. Returns -1
 * when the continuation falls off the battlefield.
 */
export function breathHex(from: number, to: number): number {
  const qOf = (h: number): number => hexCol(h) - ((hexRow(h) - (hexRow(h) & 1)) >> 1);
  const qf = qOf(from), rf = hexRow(from);
  const qt = qOf(to), rt = hexRow(to);
  const q2 = 2 * qt - qf;
  const r2 = 2 * rt - rf;
  if (r2 < 0 || r2 >= BF_HEIGHT) return -1;
  const col = q2 + ((r2 - (r2 & 1)) >> 1);
  if (col < 0 || col >= BF_WIDTH) return -1;
  return hexIndex(col, r2);
}

// -------------------------------------------------------------- on-hit hooks

export function onDamageAbilities(
  c: Ctx, content: ContentRegistry, attacker: number, defender: number,
  res: DamageResult, ranged: boolean,
): void {
  const s = c.s;
  const st = s.stacks;
  const attackerAbil = abilitiesOf(content, st.def[attacker]!);
  const defenderAlive = isAlive(st, defender);

  for (const a of attackerAbil) {
    switch (a.kind) {
      case "AGING":
        if (defenderAlive) {
          addMod(c, defender, {
            source: SRC_AGING, stat: "hp", op: "mulFp", value: 500,
            stackGroup: "aging", stackRule: "first", expires: { kind: "battle" },
          });
        }
        break;
      case "DISEASE":
        if (defenderAlive) {
          addMod(c, defender, {
            source: SRC_DISEASE, stat: "attack", op: "add", value: a.attackDelta,
            stackGroup: "disease", stackRule: "first", expires: { kind: "round", rounds: 3 },
          });
          addMod(c, defender, {
            source: SRC_DISEASE, stat: "defense", op: "add", value: a.defenseDelta,
            stackGroup: "disease", stackRule: "first", expires: { kind: "round", rounds: 3 },
          });
        }
        break;
      case "ACID_BREATH":
        if (defenderAlive) {
          // No `stackGroup`, so the fourth blow lands on armour that three
          // earlier blows have already eaten. `expires: "battle"` rather than a
          // round count: it is corrosion, not a status.
          addMod(c, defender, {
            source: SRC_ACID, stat: "defense", op: "add", value: -a.defenseDelta,
            expires: { kind: "battle" },
          });
        }
        break;
      case "BINDING":
        // Held where it stands. Nothing is rolled - Binding is not a chance
        // ability - and it is re-applied by every blow, which costs nothing
        // because the flag is idempotent. `beginTurn` decides when it lapses.
        if (defenderAlive) setFlags2(c, defender, F2_BOUND);
        break;
      case "FEAR":
        // Mindless things are not frightened, for the same reason they are not
        // blinded or hypnotised - `isMindImmune` is the one test all three ask.
        if (defenderAlive && !isMindImmune(st.flags[defender]!)
            && rollChanceBp(c, "battle.ability", a.chanceBp)) {
          setFlags2(c, defender, F2_AFRAID);
        }
        break;
      case "CONFUSION":
        if (defenderAlive && !isMindImmune(st.flags[defender]!)
            && (st.flags[defender]! & F_BERSERK) === 0
            && rollChanceBp(c, "battle.ability", a.chanceBp)) {
          // The flag and the duration together; see `STAT_BERSERK`. Setting the
          // flag alone is precisely the bug the Blood Rage spell shipped with.
          addMod(c, defender, {
            source: SRC_CONFUSION, stat: STAT_BERSERK, op: "max", value: 1,
            expires: { kind: "round", rounds: a.rounds },
          });
          setFlags(c, defender, F_BERSERK);
        }
        break;
      case "CHARM": {
        if (!defenderAlive || isMindImmune(st.flags[defender]!)) break;
        if ((st.flags[defender]! & F_HYPNOTIZED) !== 0) break;
        // The same hit-point ceiling Enthrall is gated on, and for the same
        // reason: an on-hit side-swap with no ceiling takes whatever the enemy
        // paid the most for, on a die roll, for free.
        const pool = (st.count[defender]! - 1) * st.maxHp[defender]! + st.topHp[defender]!;
        if (pool > a.maxPool) break;
        if (!rollChanceBp(c, "battle.ability", a.chanceBp)) break;
        addMod(c, defender, {
          source: SRC_CHARM, stat: STAT_HYPNOSIS, op: "max", value: 1,
          expires: { kind: "round", rounds: a.rounds },
        });
        setFlags(c, defender, F_HYPNOTIZED);
        // The side column is written, so targeting, retaliation and the turn
        // order are all right without any of them knowing this ability exists.
        jSetI32(c.j, st.side, defender, st.side[defender] === 0 ? 1 : 0);
        jSetI32(c.j, st.facing, defender, -st.facing[defender]!);
        break;
      }
      case "CURSE_ON_HIT":
        if (defenderAlive) {
          addMod(c, defender, {
            source: SRC_CURSE, stat: "damageMax", op: "set", value: st.damageMin[defender]!,
            stackGroup: "curse", stackRule: "first", expires: { kind: "round", rounds: 3 },
          });
        }
        break;
      case "POISON":
        if (defenderAlive) {
          addMod(c, defender, {
            source: SRC_POISON, stat: "hp", op: "add",
            value: -Math.max(1, roundHalfAway((st.maxHp[defender]! * a.maxPct) / 100)),
            stackGroup: "poison", stackRule: "sum", expires: { kind: "battle" },
          });
        }
        break;
      case "DISPEL_ON_HIT":
        if (defenderAlive) dispelBattleMods(c, defender);
        break;
      case "BLIND_ON_HIT":
        if (defenderAlive && !isMindImmune(st.flags[defender]!) &&
            rollChanceBp(c, "battle.ability", a.chanceBp)) {
          setFlags(c, defender, F_BLINDED);
        }
        break;
      case "FREEZING_SHOT":
        // Ranged only. Frozen borrows `F_PETRIFIED` for the lost turn, and then
        // stays BRITTLE for `rounds`: ice cracks, so the value is negative -
        // a positive `damage.taken` is a reduction (see `damage.ts`), and for a
        // while this was +500, which made a frozen stack the safest on the
        // field. Filed under its own source so the petrify cleanup in
        // `beginTurn` leaves the brittleness alone.
        if (ranged && defenderAlive && rollChanceBp(c, "battle.ability", a.chanceBp)) {
          setFlags(c, defender, F_PETRIFIED);
          addMod(c, defender, {
            source: SRC_FREEZE, stat: STAT_DAMAGE_TAKEN, op: "add", value: -VULNERABILITY_CAP_FP,
            expires: { kind: "round", rounds: a.rounds },
          });
        }
        break;
      case "PARALYZE":
        if (defenderAlive && !isMindImmune(st.flags[defender]!) &&
            rollChanceBp(c, "battle.ability", a.chanceBp)) {
          setFlags(c, defender, F_BLINDED);
        }
        break;
      case "PETRIFY":
        // Half damage while stone. The modifier lapses with the flag - in
        // `beginTurn` when the turn is lost, in `wakeOnHit` when struck - and
        // the round count is a backstop for a stack that never gets a turn.
        // It used to expire with the battle, so one Stonegaze gaze hardened the
        // victim for the rest of the fight.
        if (defenderAlive && rollChanceBp(c, "battle.ability", a.chanceBp)) {
          setFlags(c, defender, F_PETRIFIED);
          addMod(c, defender, {
            source: SRC_PETRIFY, stat: STAT_DAMAGE_TAKEN, op: "add", value: 500,
            stackGroup: "petrify", stackRule: "first",
            expires: { kind: "round", rounds: PETRIFY_ROUNDS },
          });
        }
        break;
      case "ACCURATE_SHOT":
        // The same effect as DEATH_STARE and deliberately not merged with it:
        // one is a melee gaze and this is a master_bowyer's shot, so they differ on
        // exactly the condition below and would otherwise need a flag argument.
        if (ranged && defenderAlive && (st.flags[defender]! & F_UNDEAD) === 0 &&
            rollChanceBp(c, "battle.ability", a.chanceBp)) {
          const killed = damageStack(c, defender, st.topHp[defender]!);
          creditKills(c, attacker, killed.killed);
          if (killed.died) onUnitDeathAbilities(c, content, defender, killed.killed);
        }
        break;
      case "DEATH_STARE":
        if (defenderAlive && (st.flags[defender]! & F_UNDEAD) === 0 &&
            rollChanceBp(c, "battle.ability", a.chanceBp)) {
          // Kills exactly the top creature, wounds and all.
          const killed = damageStack(c, defender, st.topHp[defender]!);
          creditKills(c, attacker, killed.killed);
          if (killed.died) onUnitDeathAbilities(c, content, defender, killed.killed);
        }
        break;
      default: break;
    }
  }

  /**
   * What a kill is worth to the thing that made it.
   *
   * Paid here rather than in `onPerkKill`, which is the ARMY's hook: this is
   * the creature's own, it reads `res.killed` which is right in front of it,
   * and it must not fire on a blow that killed nothing.
   *
   * The running total REPLACES its predecessor rather than being added beside
   * it. A stack that fights for thirty rounds lands a great many killing blows,
   * and one modifier per blow is thirty entries on the stack for one number -
   * every one of them hashed, journalled and walked by `resolveStat` on every
   * subsequent blow.
   */
  if (res.killed > 0 && isAlive(st, attacker)) {
    for (const a of attackerAbil) {
      if (a.kind !== "SOUL_HARVEST") continue;
      const had = resolveStat(0, st.mods[attacker]!.filter((m) => m.source === SRC_HARVEST)).value;
      const want = Math.min(a.cap, had + a.perKill * res.killed);
      if (want <= had) break;
      removeModsBySource(c, attacker, SRC_HARVEST as string);
      addMod(c, attacker, {
        source: SRC_HARVEST, stat: "attack", op: "add", value: want,
        stackGroup: SRC_HARVEST as string, stackRule: "highest",
        expires: { kind: "battle" },
      });
    }
  }

  /**
   * The drain, ability and spell together.
   *
   * LIFE_DRAIN is the creature's own and takes the whole blow back; Vampirism
   * puts a *share* on the stack through `STAT_LIFE_DRAIN`. They are resolved
   * here rather than in the switch above because they are one rule asked twice:
   * a Nightfeeder under the spell must drain once, at the better of the two shares,
   * not once for each source.
   *
   * It mends and never raises - `healStack` is capped at the living count - so
   * a drained blow can bring a stack back to full and never past it.
   */
  {
    let drainBp = 0;
    for (const a of attackerAbil) if (a.kind === "LIFE_DRAIN") drainBp = BP_ONE;
    for (const m of st.mods[attacker]!) {
      if (m.stat === STAT_LIFE_DRAIN && m.value > drainBp) drainBp = m.value;
    }
    if (drainBp > BP_ONE) drainBp = BP_ONE;
    if (drainBp > 0 && res.dealt > 0 && isAlive(st, attacker)) {
      const back = roundHalfAway((res.dealt * drainBp) / BP_ONE);
      if (back > 0) healStack(c, attacker, back, st.count[attacker]!);
    }
  }

  // The defender's own reactive abilities. Ember Mantle is melee-only.
  //
  // The spell and the creature ability are the same rule, so they are summed
  // rather than resolved twice: an Flamewalkers under Ember Mantle throws back both.
  if (!ranged && isAlive(st, attacker)) {
    let pct = 0;
    for (const a of abilitiesOf(content, st.def[defender]!)) {
      if (a.kind === "FIRE_SHIELD") pct += a.pct;
    }
    for (const m of st.mods[defender]!) if (m.stat === STAT_FIRE_SHIELD) pct += m.value;
    for (const a of pct > 0 ? [pct] : []) {
      const back = roundHalfAway((res.dealt * a) / 100);
      if (back <= 0) continue;
      const r = damageStack(c, attacker, back);
      c.events.push({
        type: "BattleAttacked", attacker: defender, target: attacker,
        damage: r.dealt, killed: r.killed, ranged: false, retaliation: true,
      });
      if (r.died) onUnitDeathAbilities(c, content, attacker, r.killed);
    }
  }
}

/**
 * Whether a mind spell or a mind-shaped ability passes straight through.
 *
 * The classic's mind spells - Blood Rage, Enthrall, Blind, Befuddle, Frenzy, Mirth,
 * Sorrow - and the abilities built out of the same idea do not touch anything
 * without a mind to reach: the undead, and the elementals, sentinels, rookstones
 * and engines that content marks NON_LIVING. Two flags rather than one because
 * every *other* rule that reads them disagrees - a sentinel is Resurrectable and a
 * skeleton is not, Unmake Dead bites one and not the other - so the union is
 * taken here, at the one rule where they are the same thing.
 *
 * Exported because `spells.ts` asks the same question of a `mind` spell that
 * `BLIND_ON_HIT` and `PARALYZE` ask below.
 */
export function isMindImmune(flags: number): boolean {
  return (flags & (F_UNDEAD | F_NON_LIVING)) !== 0;
}

function dispelBattleMods(c: Ctx, slot: number): void {
  const cur = c.s.stacks.mods[slot]!;
  const keep: Modifier[] = cur.filter((m) => m.expires === undefined || m.expires.kind === "permanent");
  if (keep.length !== cur.length) jSetField(c.j, c.s.stacks.mods as unknown as object, slot, keep);
}

// ------------------------------------------------------------------ on death

/**
 * RESURRECT_SELF brings the stack back once per battle with a fifth of the
 * creatures it had when it fell; DEATH_CLOUD spends the corpse on everything
 * standing next to it.
 */
export function onUnitDeathAbilities(
  c: Ctx, content: ContentRegistry, slot: number, priorCount: number,
): void {
  const s = c.s;
  const st = s.stacks;
  for (const a of abilitiesOf(content, st.def[slot]!)) {
    switch (a.kind) {
      case "RESURRECT_SELF": {
        if ((st.flags[slot]! & F_REBORN) !== 0) break;
        const back = Math.max(1, roundHalfAway(priorCount / 5));
        setFlags(c, slot, F_REBORN | F_ALIVE);
        jSetI32(c.j, st.count, slot, back);
        jSetI32(c.j, st.topHp, slot, effectiveMaxHp(s, slot));
        break;
      }
      case "DETONATION": {
        // The expansion: (90 + 5n) to every ADJACENT stack, where n is the count that
        // was standing before the blow that killed it - so a big stack makes a
        // bigger bomb. Both sides take it: an Clockwork Guard detonating in a press
        // hurts its own line, which is the whole cost of the ability.
        const blast = DETONATION_BASE + DETONATION_PER_HEAD * Math.max(0, priorCount);
        for (let i = 0; i < st.n; i++) {
          if (i === slot || !isAlive(st, i)) continue;
          if (!stacksAdjacent(st, i, slot)) continue;
          // Flat: "detonation damage is unaffected by conventional and magic
          // damage modifiers", so it does NOT go through `computeDamage`.
          const r = damageStack(c, i, blast);
          c.events.push({
            type: "BattleAttacked", attacker: slot, target: i,
            damage: r.dealt, killed: r.killed, ranged: false, retaliation: false,
          });
          if (r.died) onUnitDeathAbilities(c, content, i, r.killed);
        }
        break;
      }
      case "DEATH_CLOUD": {
        for (let i = 0; i < st.n; i++) {
          if (i === slot || !isAlive(st, i)) continue;
          if (st.side[i] === st.side[slot]) continue;
          if (!stacksAdjacent(st, i, slot)) continue;
          const r = damageStack(c, i, effectiveMaxHp(s, i));
          c.events.push({
            type: "BattleAttacked", attacker: slot, target: i,
            damage: r.dealt, killed: r.killed, ranged: false, retaliation: false,
          });
        }
        break;
      }
      default: break;
    }
  }
}

/** The expansion's own numbers for an Clockwork Guard going up. */
export const DETONATION_BASE = 90;
export const DETONATION_PER_HEAD = 5;

/** Blind, paralysis and stone break when the victim is struck. */
export function wakeOnHit(c: Ctx, slot: number): void {
  if ((c.s.stacks.flags[slot]! & F_BLINDED) !== 0) {
    clearFlags(c, slot, F_BLINDED);
    const sources = new Set<string>();
    for (const m of c.s.stacks.mods[slot] ?? []) if (m.stat === STAT_BLIND) sources.add(m.source as string);
    for (const src of [...sources].sort()) removeModsBySource(c, slot, src);
  }
  if ((c.s.stacks.flags[slot]! & F_PETRIFIED) !== 0) {
    clearFlags(c, slot, F_PETRIFIED);
    removeModsBySource(c, slot, SRC_PETRIFY as string);
  }
}

export const isTwoHexStack = (s: BattleState, i: number): boolean =>
  (s.stacks.flags[i]! & F_TWO_HEX) !== 0;
